/**
 * WARBLE — the syrinx voice. The sixth scene, ninth bird.
 *
 * Chirp provenance: Chirp/warble.html at 6f4d80a (PESSULUS — the Mindlin
 * labial oscillator into a delay-line trachea and OEC, one AudioWorklet, no
 * samples, no oscillator bank). This scene is the model's own control space:
 *
 *   PERCH — the pitch hand picks one of 13 rungs (the selection ladder).
 *   PLANE — the pitch hand sweeps β continuously; the whole register.
 *
 * Expression-hand height IS air-sac pressure: at rest, α sits below the
 * phonation threshold and the MODEL is silent. There is no gain gating in
 * this scene at all — the rest state is physics.
 */

import { createPessulus, type Pessulus } from '../audio/warble/pessulus-lite';
import { DEFAULT_GAMMA, WARBLE_PATCHES, type WarblePatch } from '../audio/warble/patches';
import { F0, formatF0, PERCH_B, regimeOf, THR } from '../music/warble';
import { WarbleAdapter } from '../mapping/adapters';
import type { WarbleMode } from '../mapping/mappers';
import type { ControlState, FeatureFrame, WarbleIntent } from '../mapping/types';
import { chips, el, panel, slider } from '../ui/controls';
import type { Scene, SceneContext } from './types';

export function createWarbleScene(): Scene {
  let engine: Pessulus | null = null;
  let ctxRef: SceneContext | null = null;
  const adapter = new WarbleAdapter();
  let last: WarbleIntent | null = null;
  let patch: WarblePatch = WARBLE_PATCHES[0];
  let gamma = DEFAULT_GAMMA;
  let guard = 0;
  let sounding = false;
  let ready = false;

  function applyPatch(p: WarblePatch): void {
    patch = p;
    adapter.setFallbackBeta(p.beta);
    engine?.setStructure({ gamma, refl: p.refl, spread: p.spread });
    engine?.setGrit(p.grit);
    engine?.setFold(p.fold);
    ctxRef?.setStatus(p.hint);
  }

  return {
    id: 'warble',
    label: 'Warble',
    blurb: 'A physical model of a bird syrinx — three equations, no samples. Height is breath pressure; the pitch hand is labial tension. Make a fist to overblow it.',
    padMode: 'xy',
    // PERCH: thirteen rungs on the height axis, straight onto the ladder.
    selectionSteps: PERCH_B.length,

    async mount(host, ctx) {
      ctxRef = ctx;
      ready = false;
      engine = await createPessulus(ctx.bus.ctx, ctx.bus.input);
      if (!ctxRef) { engine.dispose(); engine = null; return; } // unmounted mid-await
      engine.onDiag((d) => { guard = d.guard; });
      applyPatch(patch);
      ready = true;

      host.append(panel('Pessulus', 'The Mindlin syrinx: air-sac pressure against labial tension. Below the phonation threshold there is only breath — silence here is the model, not a mute.', [
        chips<WarbleMode>({
          label: 'Pitch axis', value: adapter.getMode(),
          options: [
            { id: 'perch', label: 'Perch', title: '13 quantized rungs — the ladder shows your rung' },
            { id: 'plane', label: 'Plane', title: 'Continuous tension — the whole register in one sweep' },
          ],
          onChange: (m) => {
            adapter.setMode(m);
            ctx.setStatus(m === 'perch'
              ? 'Perch — thirteen rungs. The ladder shows what your pitch hand will sound.'
              : 'Plane — continuous tension. Slide the pitch hand for sweeps.');
          },
        }),
        chips<string>({
          label: 'Voice', value: patch.id,
          options: WARBLE_PATCHES.map((p) => ({ id: p.id, label: p.label, title: p.hint })),
          onChange: (id) => {
            const next = WARBLE_PATCHES.find((p) => p.id === id);
            if (next) applyPatch(next);
          },
        }),
        slider({
          label: 'Register', min: 3000, max: 32000, step: 500, value: gamma,
          format: (v) => `γ ${Math.round(v / 1000)}k`,
          onInput: (v) => { gamma = v; engine?.setStructure({ gamma: v }); },
        }),
        el('p', { class: 'note' }, [
          'No samples anywhere in this scene. Every voice is the same three equations at a different transpose.',
        ]),
      ]));
      ctx.setStatus(patch.hint);
    },

    update(frame: FeatureFrame, control: ControlState) {
      if (!engine || !ready) return;
      const fistEdge = adapter.fistEdge(control);
      const intent = adapter.update(frame, control);
      last = intent;

      engine.setAlpha(intent.alpha);
      engine.setBeta(intent.beta);
      engine.setFold(Math.max(intent.fold, patch.fold));
      engine.setGrit(Math.max(intent.grit, patch.grit));
      // The rim's structural half, on the fist EDGE only: overblow couples the
      // source to the tract harder (negative reflection), release restores the
      // patch's own reflection.
      if (fistEdge) engine.setStructure({ refl: control.fist ? -0.6 : patch.refl });

      if (intent.sounding !== sounding) {
        sounding = intent.sounding;
        engine.gate(sounding);
      }
      if (intent.rearticulate) engine.rearticulate();
    },

    selection() {
      // The ladder answers "what would sound" — only meaningful when the axis
      // is quantized. PLANE is continuous; the f₀ readout carries it there.
      if (adapter.getMode() !== 'perch' || !last) return null;
      const rung = PERCH_B.indexOf(last.beta);
      if (rung < 0) return null;
      return { count: PERCH_B.length, active: rung, label: `${formatF0(F0(gamma, last.beta))} Hz` };
    },

    readouts() {
      const audible = last ? last.alpha > THR(last.beta) : false;
      return [
        ['f₀', last && audible ? `${Math.round(F0(gamma, last.beta))} Hz` : 'silent'],
        ['regime', last ? regimeOf(last.alpha, last.beta) : '—'],
        ['α', last ? last.alpha.toFixed(3) : '—'],
        ['β', last ? last.beta.toFixed(3) : '—'],
        ['guard', String(guard)],
      ];
    },

    unmount() {
      engine?.gate(false);
      engine?.dispose();
      engine = null;
      ctxRef = null;
      adapter.reset();
      sounding = false;
    },
  };
}
